import { PlusCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Modal from '../ui/Modal';
import type { SuccessData } from './types';

interface OperationSuccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: SuccessData | null;
}

export function OperationSuccessModal({ isOpen, onClose, data }: OperationSuccessModalProps) {
  const { t } = useTranslation();

  if (!data) return null;

  // In BUY: we receive fiat (clientGives), give crypto (clientGets)
  // In SELL: we receive crypto (clientGets), give fiat (clientGives)
  const isSell = data.mode.toLowerCase() === 'sell';
  const receivedAmount   = isSell ? data.clientGetsAmount   : data.clientGivesAmount;
  const receivedCurrency = isSell ? data.clientGetsCurrency  : data.clientGivesCurrency;
  const givenAmount      = isSell ? data.clientGivesAmount   : data.clientGetsAmount;
  const givenCurrency    = isSell ? data.clientGivesCurrency : data.clientGetsCurrency;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('cashier.successModal.title')}
      size="sm"
    >
      <div className="py-1">
        {/* Success Header */}
        <div className="flex items-center gap-4 mb-6">
          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-ctp-teal to-[#115e59] text-white flex items-center justify-center flex-shrink-0">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          </div>
          <div className="flex flex-col gap-0.5">
            <span className="text-xl font-bold text-foreground">
              {t('cashier.successModal.success')}
            </span>
            <span className="text-[13px] text-foreground-muted">
              {t('cashier.successModal.transactionProcessed')}
            </span>
          </div>
        </div>

        {/* Transaction Summary */}
        <div className="bg-surface-hover rounded-lg p-4 mb-5">
          {/* Summary Header */}
          <div className="flex justify-between items-center mb-4 pb-3 border-b border-border">
            <span className="text-[11px] font-semibold text-foreground-muted uppercase tracking-wide">
              {t('cashier.successModal.transactionSummary')}
            </span>
            <span className={`
              text-[11px] font-bold py-1 px-2.5 rounded uppercase
              ${data.mode.toLowerCase() === 'buy'
                ? 'bg-ctp-green/15 text-ctp-green'
                : 'bg-ctp-red/15 text-ctp-red'
              }
            `}>
              {data.mode}
            </span>
          </div>

          {/* Amounts */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex flex-col items-center flex-1">
              <span className="text-[10px] font-semibold text-foreground-muted uppercase tracking-wide mb-1">
                {t('cashier.successModal.received', 'Received')}
              </span>
              <span className="text-[22px] font-bold text-foreground font-sans">
                {receivedAmount}
              </span>
              <span className="text-xs font-semibold text-ctp-teal mt-0.5">
                {receivedCurrency}
              </span>
            </div>

            <div className="text-foreground-muted px-2">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
            </div>

            <div className="flex flex-col items-center flex-1">
              <span className="text-[10px] font-semibold text-foreground-muted uppercase tracking-wide mb-1">
                {t('cashier.successModal.given', 'Given')}
              </span>
              <span className="text-[22px] font-bold text-foreground font-sans">
                {givenAmount}
              </span>
              <span className="text-xs font-semibold text-ctp-teal mt-0.5">
                {givenCurrency}
              </span>
            </div>
          </div>

          {/* Details Grid */}
          <div className="grid grid-cols-3 gap-3 pt-3 border-t border-border">
            <div className="flex flex-col gap-0.5">
              <span className="text-[10px] font-semibold text-foreground-muted uppercase tracking-wide">
                {t('cashier.successModal.rate')}
              </span>
              <span className="text-sm font-semibold text-foreground font-mono">
                {(() => {
                  const rateNum = parseFloat(data.rate);
                  if (!rateNum || rateNum <= 0) return data.rate;
                  // BUY mode: rate is EUR/USDT, show as "1 EUR = X USDT"
                  if (data.mode.toLowerCase() === 'buy') {
                    return `1 ${data.clientGivesCurrency} = ${(1 / rateNum).toFixed(4)} ${data.clientGetsCurrency}`;
                  }
                  // SELL mode: rate is EUR/USDT, show as "1 USDT = X EUR"
                  return `1 ${data.clientGetsCurrency} = ${rateNum.toFixed(4)} ${data.clientGivesCurrency}`;
                })()}
              </span>
            </div>
            <div className="flex flex-col gap-0.5">
              <span className="text-[10px] font-semibold text-foreground-muted uppercase tracking-wide">
                {t('cashier.successModal.date', 'Date')}
              </span>
              <span className="text-sm font-semibold text-foreground font-mono">
                {data.transactionDate ? new Date(data.transactionDate).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-'}
              </span>
            </div>
            <div className="flex flex-col gap-0.5">
              <span className="text-[10px] font-semibold text-foreground-muted uppercase tracking-wide">
                {t('cashier.successModal.client')}
              </span>
              <span className="text-sm font-semibold text-foreground font-mono">
                {data.clientId}
              </span>
            </div>
          </div>
        </div>

        {/* Done Button */}
        <button
          className="flex items-center justify-center gap-2 w-full py-3.5 px-6 bg-ctp-teal text-black border-none rounded-md text-sm font-semibold cursor-pointer transition-colors hover:bg-[#0f766e]"
          onClick={onClose}
        >
          <PlusCircle size={18} />
          {t('cashier.successModal.newTransaction')}
        </button>
      </div>
    </Modal>
  );
}
